import { Video, ExternalLink, Clock, AlertCircle, VideoIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { getStatusColor, getStatusIcon } from '../utils/uploadValidation';

export function VideoTable({ videos, loading }) {
  if (loading) {
    return <div className="text-center py-8 text-gray-500">Loading videos...</div>;
  }

  if (videos.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg">
        <Video size={48} className="mx-auto mb-3 opacity-30" />
          <div className="fixed inset-0 w-screen h-screen bg-black/100 backdrop-blur-sm z-[9999] flex flex-col items-center justify-center">
          {/* The Spinner Container */}
          <div className="relative w-20 h-20 mb-6">
            {/* Outer Glowing Ring */}
            <div className="absolute inset-0 rounded-full border-4 border-white/5"></div>
            
            {/* Spinning Gradient Ring */}
            <div className="absolute inset-0 rounded-full border-t-4 border-purple-500 animate-spin shadow-[0_0_15px_rgba(168,85,247,0.5)]"></div>
            
            {/* Inner Icon */}
            <div className="absolute inset-0 flex items-center justify-center">
              <VideoIcon size={28} className="text-white/30 animate-pulse" />
            </div>
          </div>

          {/* Status Text */}
          <div className="text-center space-y-2">
            <p className="text-white/80 text-lg font-medium tracking-tight">
              Melody is Loading
            </p>
            <p className="text-white/40 text-xs tracking-[0.2em] uppercase">
              Please wait a moment
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto bg-white rounded-lg shadow">
      <table className="w-full">
        <thead className="bg-gray-100 border-b">
          <tr>
            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Title</th>
            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Description</th>
            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
            {/* Added Header Column */}
            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Feedback</th>
            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Uploaded</th>
            <th className="px-6 py-3 text-center text-sm font-semibold text-gray-700">Action</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {videos.map((item, idx) => (
            <motion.tr
              key={item.key}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
              className="hover:bg-gray-50"
            >
              <td className="px-6 py-4">
                <p className="font-medium text-gray-900 truncate">{item.name}</p>
              </td>
              <td className="px-6 py-4">
                <p className="text-gray-600 truncate text-sm">{item.description || 'No description'}</p>
              </td>
              <td className="px-6 py-4">
                <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(item.status)}`}>
                  {getStatusIcon(item.status)} {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                </span>
              </td>
              
              {/* Added Feedback Column Logic */}
              <td className="px-6 py-4">
                {item.status === 'rejected' && item.rejectionReason ? (
                  <div className="flex items-start gap-1.5 text-xs text-red-600 bg-red-50 p-2 rounded border border-red-100 min-w-[150px]">
                    <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                    <span className="leading-tight">{item.rejectionReason}</span>
                  </div>
                ) : (
                  <span className="text-gray-300 text-xs italic">No feedback</span>
                )}
              </td>

              <td className="px-6 py-4">
                <div className="flex items-center gap-2 text-sm text-gray-600 whitespace-nowrap">
                  <Clock size={16} />
                  {new Date(item.uploadedAt).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </div>
              </td>
              <td className="px-6 py-4 text-center">
                <a
                  href={item.youtubeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg text-xs font-medium transition"
                >
                  <ExternalLink size={14} />
                  Watch
                </a>
              </td>
            </motion.tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}